/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import jsonExport from "jsonexport/dist";

import {
  Card,
  Divider,
  Grid,
  Hidden,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";

import {
  BulkActionsToolbar,
  BulkExportButton,
  Datagrid,
  DateField,
  DateInput,
  downloadCSV,
  FilterButton,
  ListContextProvider,
  ListToolbar,
  NumberField,
  RefreshButton,
  TextField,
  Title,
  TopToolbar,
  useListContext,
  useNotify,
  useRedirect,
} from "react-admin";

import { logError } from "../../../utils/appUtils";
import { InvoiceStatus } from "../../../utils/constants";

import { ChevronRightIcon, ExportIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { ShowButton } from "../../../components/common/ShowButton";
import SimpleList from "../../../components/common/SimpleList";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { InvoicePeak } from "../../../managerApp/resources/invoices/InvoicePeak";
import { InvoiceStatusIcon } from "../../../managerApp/resources/invoices/InvoiceStatusIcon";
import {
  getMemo,
  getServiceDate,
} from "../../../managerApp/resources/invoices/InvoiceUtils";
import { useUser } from "../../../authenticatorApp/UserContext";

const useStyles = makeStyles((theme) => ({
  listRoot: {
    transition: theme.transitions.create(["all"], {
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginTop: 0,
  },
  bulkActions: {
    // [classes.bulkActions] remove to restore original behavior
    marginTop: -theme.spacing(8),
    transition: theme.transitions.create("margin-top"),
  },
  totals: {
    marginLeft: theme.spacing(2),
  },
}));

const tabs = [
  { id: InvoiceStatus.PENDING, name: "Pending", showSmall: true },
  { id: InvoiceStatus.PAID, name: "Completed", showSmall: true },
];

const exporter = (invoices, notify, onUnselectItems) => {
  const invoicesForExport = [];

  const handleUndefined = (item) => (item ? item : "");

  try {
    invoices.forEach((invoice) => {
      invoice.lineItems.items.forEach((element) => {
        invoicesForExport.push({
          InvoiceNo: invoice.invoiceNumber,
          Customer: invoice.accountName,
          InvoiceDate: new Date(invoice.createdAt).toLocaleDateString(),
          DueDate: new Date(invoice.dueDate).toLocaleDateString(),
          Terms: invoice.paymentTerms,
          Memo: getMemo(invoice),
          Item: element.service?.name || "",
          ItemDescription: handleUndefined(element.serviceNote),
          ItemQuantity: element.quantity,
          ItemRate: element.unitCost,
          ItemAmount: element.amount,
          ItemTaxCode: element.taxRate.taxCode,
          ItemTaxAmount: element.itemTax,
          Currency: "USD",
          ServiceDate: getServiceDate(invoice, element),
        });
      });
    });

    jsonExport(
      invoicesForExport,
      {
        rename: [
          "InvoiceNo",
          "Customer",
          "InvoiceDate",
          "DueDate",
          "Terms",
          "Memo",
          "Item(Product/Service)",
          "ItemDescription",
          "ItemQuantity",
          "ItemRate",
          "ItemAmount",
          "ItemTaxCode",
          "ItemTaxAmount",
          "Currency",
          "ServiceDate",
        ], // order fields in the export
      },
      (err, csv) => {
        downloadCSV(csv, `invoices_${new Date().toLocaleDateString()}`); // download as 'invoices_date.csv` file
      }
    );
  } catch (err) {
    logError("invoices:InvoiceList:exporter", err, notify);
  } finally {
    onUnselectItems();
  }
};

const InvoiceSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { invoiceNumber: { matchPhrasePrefix: `.*${value}.*` } },
            { invoiceNumber: { wildcard: `*${value}*` } },
            { poNumbers: { matchPhrasePrefix: `${value}` } },
            { poNumbers: { wildcard: `*${value}*` } },
            { railCarNumbers: { matchPhrasePrefix: `${value}` } },
            { railCarNumbers: { wildcard: `*${value}*` } },
            { pickupNumbers: { matchPhrasePrefix: `${value}` } },
            { pickupNumbers: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (tabValue) => {
  return [
    <InvoiceSearchField label="Search" tabValue={tabValue} alwaysOn />,
    <DateInput
      source="createdAt.lte"
      label="Issued Before..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
    <DateInput
      source="createdAt.gte"
      label="Issued After..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
    <DateInput
      source="dueDate.lte"
      label="Due Before..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
    <DateInput
      source="dueDate.gte"
      label="Due After..."
      variant="outlined"
      resettable
      onKeyPress={(event) => event.preventDefault()}
    />,
  ];
};

const BulkListActions = ({ tabValue, ...props }) => {
  const notify = useNotify();
  const { onUnselectItems } = useListContext();

  return (
    <BulkExportButton
      {...props}
      exporter={(data) => exporter(data, notify, onUnselectItems)}
      color="default"
      icon={<ExportIcon />}
    />
  );
};

const ListActions = (props) => {
  const { isXSmall } = props;

  return !isXSmall ? (
    <TopToolbar>
      <FilterButton />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

const getSessionTab = (appUser) => {
  const sessionTab = Number(
    sessionStorage.getItem(`InvoicesTab_${appUser.id}`)
  );
  if (sessionTab && tabs[sessionTab]) {
    return sessionTab;
  }
  return 0; // Default to the first visible tab if the sessionTab is not valid
};

export const InvoiceList = (props) => {
  const { appUser } = useUser();
  const classes = useStyles();
  const isMedium = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const [tabValue, setStateTabValue] = useState(getSessionTab(appUser));

  const [defaultFilter, setDefaultFilter] = useState({
    accountId: appUser.accountId,
    activeStatusDueDate: {
      beginsWith: {
        active: "true",
        status: tabs[tabValue].id,
      },
    },
  });
  const [defaultSearchFilter, setDefaultSearchFilter] = useState({
    accountId: { eq: appUser.accountId },
    active: { eq: "true" },
    status: { eq: tabs[tabValue].id },
  });

  const setTabValue = (value) => {
    debugger;
    setStateTabValue(value);
    setDefaultFilter({
      accountId: appUser.accountId,
      activeStatusDueDate: {
        beginsWith: {
          active: "true",
          status: tabs[value].id,
        },
      },
    });
    setDefaultSearchFilter({
      accountId: { eq: appUser.accountId },
      active: { eq: "true" },
      status: { eq: tabs[value].id },
    });
  };

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"invoiceByAccountByActiveByStatusByDate", "search":"dueDate"}`,
    order: "DESC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`)
  );
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? sessionPerPage : 10
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Invoices",
    defaultFilter: defaultFilter,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    defaultSort: defaultSort,
    resource: "invoices",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Invoices" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(tabValue)}
        actions={<ListActions isXSmall={isXSmall} />}
      />
      <Card
        className={classnames(
          classes.listRoot,
          { [classes.bulkActions]: listContext.selectedIds.length > 0 }
          // [classes.bulkActions] remove to restore original behavior
        )}
      >
        <BulkActionsToolbar>
          <BulkListActions tabValue={tabValue} />
        </BulkActionsToolbar>
        <InvoiceTabs
          {...props}
          {...listContext}
          appUser={appUser}
          tabValue={tabValue}
          setTabValue={setTabValue}
          isXSmall={isXSmall}
          isMedium={isMedium}
        />
        <PaginationToolbar {...props} />
      </Card>
    </ListContextProvider>
  );
};

const PaginationToolbar = (props) => {
  const classes = useStyles();
  const listContext = useListContext(props);

  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const [totals, setTotals] = useState({ subtotal: 0, tax: 0, total: 0 });

  useEffect(() => {
    debugger;
    if (listContext?.ids?.length > 0 && listContext?.data) {
      let subtotal = 0,
        tax = 0,
        total = 0;

      listContext.ids?.forEach((id) => {
        subtotal += listContext.data[id].subtotal;
        tax += listContext.data[id].tax;
        total += listContext.data[id].total;
      });

      setTotals({ subtotal, tax, total });
    }
  }, [listContext]);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Hidden mdDown>
        <Grid item md={5} lg={5}>
          <Typography className={classes.totals}>
            {`SUBTOTAL: ${formatter.format(
              totals.subtotal
            )}, TAX: ${formatter.format(totals.tax)}, TOTAL: ${formatter.format(
              totals.total
            )}`}
          </Typography>
        </Grid>
      </Hidden>
      <Grid item xs={12} md={7}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const InvoiceTabs = ({ tabValue, setTabValue, setPage, ...props }) => {
  const { isXSmall, appUser } = props;

  const handleChange = (event, value) => {
    sessionStorage.setItem(`InvoicesTab_${appUser.id}`, value);
    setPage(1);
    setTabValue(value);
  };

  return (
    <>
      <Tabs
        value={Number(tabValue)}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
      >
        {tabs.map(
          (itm, idx) =>
            (!isXSmall || itm.showSmall) && (
              <Tab key={itm.id} label={itm.name} id={`invoice-${idx}`} />
            )
        )}
      </Tabs>
      <Divider />
      {tabs.map(
        (itm) =>
          itm.id === tabs[tabValue].id && (
            <InvoiceGrid {...props} key={itm.id} tabValue={itm.id} />
          )
      )}
    </>
  );
};

const InvoiceGrid = ({ ids, page, tabValue, ...props }) => {
  const { isXSmall, isMedium } = props;
  const redirect = useRedirect();
  const hasBulkActions = true;

  const showInvoice = (jsonRecord, basePath, record) => {
    if (record.externalUrl) {
      window.open(record.externalUrl, "_blank");
    } else {
      redirect(`/invoices/${record.id}/show`);
    }
  };

  return isXSmall ? (
    <SimpleList
      primaryText={(record) => record.invoiceNumber}
      secondaryText={(record) =>
        `Due: ${new Date(record.dueDate).toLocaleDateString()}`
      }
      tertiaryText={(record) =>
        record.total.toLocaleString(undefined, {
          style: "currency",
          currency: "USD",
        })
      }
      leftIcon={(record) => <InvoiceStatusIcon record={record} />}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType={false}
      rowClick={(record) => showInvoice(null, null, record)}
    />
  ) : isMedium ? (
    <Datagrid {...props} rowClick={showInvoice} hasBulkActions={hasBulkActions}>
      <TextField source="invoiceNumber" label="Invoice No." sortable={true} />
      <NumberField
        source="total"
        label="Amount"
        sortable={true}
        options={{
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }}
      />
      {tabValue === InvoiceStatus.PENDING ? (
        <NumberField
          source="balance"
          label="Balance"
          sortable={false}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ) : null}
      <InvoiceStatusIcon source="status" label="Status" sortable={false} />
    </Datagrid>
  ) : (
    <Datagrid
      {...props}
      rowClick={showInvoice}
      hasBulkActions={hasBulkActions}
      expand={<InvoicePeak />}
    >
      <TextField source="poNumber" label="PO Number" sortable={true} />
      <TextField source="invoiceNumber" label="Invoice No." sortable={true} />
      <NumberField
        source="total"
        label="Amount"
        sortable={true}
        options={{
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 2,
        }}
      />
      {tabValue === InvoiceStatus.PENDING ? (
        <NumberField
          source="balance"
          label="Balance"
          sortable={false}
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />
      ) : null}
      <DateField
        source="dueDate"
        sortable={true}
        sortBy={`{"key":"invoiceByAccountByActiveByStatusByDate", "search":"dueDate"}`}
      />
      <InvoiceStatusIcon source="status" label="Status" sortable={false} />
      {tabValue === InvoiceStatus.PENDING ? (
        <DateField source="sentDate" label="Sent" sortable={true} />
      ) : null}
      {tabValue === InvoiceStatus.PAID ? (
        <DateField source="paidDate" label="Completed" sortable={true} />
      ) : null}
      <ShowButton label="View" />
    </Datagrid>
  );
};
